<script>
   /****************************************************************/
   /* PlotPLSLoadings                                              */
   /* --------------------                                         */
   /* shows plot with PLS loadings                                 */
   /****************************************************************/

   import { Segments } from 'svelte-plots-basic/2d';
   import { Vector, vector } from 'mdatools/arrays';
   import { plsmodel_get } from '@mdatools-apps/wasm/pls.js';

   import { AXIS_COLOR, LOADINGS_LINE_COLOR } from './Defaults.js';
   import Axes from './Axes.svelte';
   import { csvRow, downloadCSV } from '../utils.js';
   import PlotSeries from './PlotSeries.svelte';


   let {
      params,                        // JSON with main plot parameters
      callbacks,                     // JSON with all callback methods
      props,                         // JSON with main properties (model, results, dataset)
      selected,                      // ID of selected object
   } = $props();

   // get model object from properties
   let model = $derived(props ? props.model : null);

   /**
    * Gets vector with values for particular outcome and component from the model object
    * @param {Object} m - model object.
    * @param {Number} a - component index (from 1);
    */
   function getValues(m, a, type) {
      if (!m) return null;
      if (!a || a < 1 || a > m.ncomp) return null;
      return new Vector(plsmodel_get(m, type, a))
   }

   // ranges of values to define axis limits (for two series, X and Y loadings)
   let xRange = $state();
   let yRange = $state();

   // get values and indices for x-variables
   const xvalues = $derived(model ? vector(model.xvarvalues) : null);
   const xindices = $derived(model ? vector(model.xvarindices) : null);
   const xlabels = $derived(model ? model.xvarlabels : null);

   // get x-loadings
   const xLoadings2 = $derived(model ? getValues(model, params.comp2, params.statType) : null);
   const xLoadings1 = $derived(model ? getValues(model, params.comp1, params.statType) : null);

   // define what to use as values and label for x-axis depending on the plot type
   const x = $derived.by(() => {
      if (!model || !params.comp1) return null;

      if (params.plotType === 'p') {
         return {
            values: xLoadings1,
            label: `LV${params.comp1}`
         };
      }

      if (params.plotType === 'l' && xvalues) {
         return {
            values: xvalues,
            label: model.varvaluesName + (model.varvaluesUnits ? ', ' + model.varvaluesUnits : '')
         };
      }

      return {
         values: xindices,
         label: 'Predictors',
      }
   });

   // get values and label for x-component
   const xValues = $derived(x ? x.values : null);
   const xLabel = $derived(x ? x.label : null);

   // get values and label for y-component
   const yValues = $derived(xLoadings2);
   const yLabel = $derived(`LV${params.comp2}`);

   // reactive expression to compute y-axis limits
   const limY = $derived.by(() => {
      if (yRange && yRange[0] < Number.POSITIVE_INFINITY && yRange[1] > Number.NEGATIVE_INFINITY) {
         const dY = (yRange[1] - yRange[0]) * 0.10;
         return [yRange[0] - 2 * dY, yRange[1] + dY];
      } else {
         return [0, 1];
      }
   });

   // reactive expression to compute x-axis limits
   const limX = $derived.by(() => {
      if (xRange && xRange[0] < Number.POSITIVE_INFINITY && xRange[1] > Number.NEGATIVE_INFINITY) {
         const dX = Math.abs((xRange[1] - xRange[0]) * 0.10);
         return params.plotType === 'l' && model.varrev ? [xRange[1] + dX, xRange[0] - dX] : [xRange[0] - dX, xRange[1] + dX];
      } else {
         return [0, 1];
      }
   });

   // title and filename
   const title = $derived(params.statType === 'xloadings' ? 'X-loadings' : 'Weights');
   const fileName = $derived('plot-' + params.statType);

   // method for creating CSV version of the plot data
   function downloadCSVCallback() {
      const { sep, dec } = Object.values(props.data)[0];
      let out = csvRow(['Labels', xLabel, yLabel], sep, dec);
      for (let i = 0; i < xlabels.length; i++) {
         out += csvRow([xlabels[i], xValues.v[i], yValues.v[i]], sep, dec);
      }
      downloadCSV(out, fileName);
   }

</script>

{#if xValues && yValues }
<Axes {xLabel} {yLabel} {title} {fileName} {downloadCSVCallback} {limX} {limY}>

   <!-- axis lines (x = 0 and y = 0) -->
   <Segments xStart={[limX[0]]} xEnd={[limX[1]]} yStart={[0]} yEnd={[0]} lineColor={AXIS_COLOR} />
   {#if params.plotType === 'p'}
   <Segments xStart={[0]} xEnd={[0]} yStart={[limY[0]]} yEnd={[limY[1]]} lineColor={AXIS_COLOR} />
   {/if}

   <!-- data points  -->
   <PlotSeries
      {xValues}
      {yValues}
      {selected}
      plotType={params.plotType}
      showLabels={params.showLabels}
      labels={xlabels}
      shortenLabels={false}
      mainColor={LOADINGS_LINE_COLOR}
      markerWidth={2.0}
      bind:xRange={xRange}
      bind:yRange={yRange}
   />
</Axes>
{/if}
